import { useState } from 'react';
import { Button, TextInput } from '@c2fo/liquidity';
import { createLocation } from '../../api/Locations';
import { CredentialSelector } from '../CredentialSelector';
import { useApiContext } from '../../contexts/FileDeliveryContext';
import type { Location } from '../../api/Locations/types';
import { Modal } from '../Modal';
import { FormField } from '../FormField';

interface NewLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (location: Location) => void;
  onError: (message: string) => void;
}

export const NewLocationModal: React.FC<NewLocationModalProps> = ({ isOpen, onClose, onCreated, onError }) => {
  const apiContext = useApiContext();
  const [displayName, setDisplayName] = useState('');
  const [uri, setUri] = useState('');
  const [description, setDescription] = useState('');
  const [credential, setCredential] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const isFormValid = () => {
    return displayName.trim() && uri.trim();
  };

  const handleSave = async () => {
    if (!displayName.trim()) {
      onError('Display name is required');
      handleClose();
      return;
    }

    if (!uri.trim()) {
      onError('URI is required');
      handleClose();
      return;
    }

    setIsSaving(true);
    try {
      const location = await createLocation(apiContext, {
        location: {
          name: '',
          displayName: displayName.trim(),
          uri: uri.trim(),
          description: description.trim() || undefined,
          credential: credential || undefined,
        },
      });

      onCreated(location);
      handleClose();
    } catch {
      onError('Failed to create location');
      handleClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setDisplayName('');
    setUri('');
    setDescription('');
    setCredential('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="New Location"
      footer={
        <>
          <Button variant="tertiary" className="rounded-md" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            className="rounded-md text-white"
            onClick={handleSave}
            disabled={isSaving || !isFormValid()}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </>
      }
    >
      <FormField label="Display Name" required>
        <TextInput
          value={displayName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDisplayName(e.target.value)}
          placeholder="Enter a name for this location"
          className="w-full"
          size="sm"
        />
      </FormField>

      <FormField label="URI" required>
        <TextInput
          value={uri}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUri(e.target.value)}
          placeholder="e.g., s3://bucket-name/path or sftp://host/path"
          className="w-full"
          size="sm"
        />
      </FormField>

      <FormField label="Description">
        <TextInput
          value={description}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
          placeholder="Enter an optional description"
          className="w-full"
          size="sm"
        />
      </FormField>

      <FormField label="Credential">
        <CredentialSelector
          value={credential}
          onChange={setCredential}
          onError={onError}
          placeholder="Select an credential (optional)"
        />
      </FormField>
    </Modal>
  );
};
